import React, { useEffect, useState } from 'react';
import {
  Snackbar,
  Alert,
  Button,
  Box,
  Typography,
  IconButton,
  Slide,
  SlideProps
} from '@mui/material';
import {
  SystemUpdateAlt as UpdateIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

interface UpdateNotificationProps {
  onUpdate?: () => void;
}

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Listen for PWA update events
    const handleUpdateAvailable = () => {
      console.log('[PWA] Update available');
      setUpdateAvailable(true);
      setOpen(true);
    };

    const handleUpdateInstalled = () => {
      console.log('[PWA] Update installed');
      setUpdateAvailable(false);
      setOpen(false);
    };

    // Listen for custom events from service worker
    window.addEventListener('pwa-update-available', handleUpdateAvailable);
    window.addEventListener('pwa-update-installed', handleUpdateInstalled);

    return () => {
      window.removeEventListener('pwa-update-available', handleUpdateAvailable);
      window.removeEventListener('pwa-update-installed', handleUpdateInstalled);
    };
  }, []);

  const handleUpdate = () => {
    if (onUpdate) {
      onUpdate();
    } else {
      // Default behavior: reload the page
      window.location.reload();
    }
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  if (!updateAvailable) {
    return null;
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      TransitionComponent={SlideTransition}
      sx={{
        bottom: { xs: 80, sm: 24 }, // Account for mobile bottom navigation
        '& .MuiSnackbarContent-root': {
          padding: 0,
        },
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<UpdateIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              onClick={handleUpdate}
              startIcon={<RefreshIcon />}
              sx={{
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              Update
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          width: '100%',
          maxWidth: 600,
          backgroundColor: '#1976d2',
          color: 'white',
          boxShadow: 3,
          '& .MuiAlert-icon': {
            color: 'white',
          },
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            New Version Available
          </Typography>
          <Typography variant="body2">
            A new version of DesignOne is ready. Update now for the latest features and improvements.
          </Typography>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default UpdateNotification;
